// Abnahme / Gewährleistung / Bürgschaften date math, client twin of utils/warranty/calc.js (live
// previews while editing; the server stays authoritative and knows the public holidays of the
// company's Bundesland). Keep everything below the header identical to the server file:
// test/warranty/calc.test.js compares both.

const CONTRACT_BASES = ["vob", "bgb", "bgb_consumer"];
// formal = förmliche Abnahme with a joint protocol (§ 12 Abs. 4 VOB/B; by agreement under BGB);
// implied = konkludente Abnahme (e.g. moving in and paying without complaint), recorded by staff;
// fictitious_completion / fictitious_use = fiktive Abnahme § 12 Abs. 5 Nr. 1 / Nr. 2 VOB/B;
// fictitious_deadline = fiktive Abnahme § 640 Abs. 2 BGB (deadline set after completion).
const ACCEPTANCE_METHODS = [
  "formal",
  "implied",
  "fictitious_completion",
  "fictitious_use",
  "fictitious_deadline"
];
const FICTITIOUS_METHODS = ["fictitious_completion", "fictitious_use", "fictitious_deadline"];
const ACCEPTANCE_STATUSES = ["draft", "requested", "accepted", "refused"];
const DEFECT_STATUSES = ["open", "fixed", "accepted"];
const CLAIM_STATUSES = ["open", "in_progress", "fixed", "accepted", "rejected"];
const GUARANTEE_TYPES = ["contract_performance", "warranty", "advance_payment"];
const GUARANTEE_DIRECTIONS = ["given", "received"];
const GUARANTEE_STATUSES = ["active", "return_requested", "returned", "drawn"];
// "angemessene Frist" of § 640 Abs. 2 BGB: the law names no number; two weeks is the common
// suggestion (12 Werktage like § 12 Abs. 5 Nr. 1 VOB/B is about the same). Editable per case.
const DEFAULT_ACCEPTANCE_DEADLINE_DAYS = 14;
// The Gewährleistungsbegehung is suggested this many months before a warranty period ends.
const INSPECTION_LEAD_MONTHS = 3;
const MAX_WARRANTY_MONTHS = 360;
const MAX_CENTS = 100000000000;

/**
 * Statutory warranty periods (Verjährung der Mängelansprüche) in months by kind of work and
 * contract basis. bgb_consumer uses the BGB periods: VOB/B is not privileged against consumers
 * (§ 310 Abs. 1 S. 3 BGB), its 4 years would not hold in a consumer contract.
 *
 * - building: Bauwerk / Arbeiten an einem Bauwerk.
 *   VOB/B § 13 Abs. 4 Nr. 1 S. 1: 4 Jahre; BGB § 634a Abs. 1 Nr. 2: 5 Jahre.
 * - maintenance_parts: Teile maschineller und elektrotechnischer/elektronischer Anlagen, bei
 *   denen die Wartung Einfluss auf Sicherheit und Funktionsfähigkeit hat, wenn der
 *   Auftraggeber die Wartung für die Dauer der Frist nicht dem Auftragnehmer übertragen hat.
 *   VOB/B § 13 Abs. 4 Nr. 2: 2 Jahre; BGB has no such rule: as part of the Bauwerk 5 Jahre.
 * - fire_parts: vom Feuer berührte Teile von Feuerungsanlagen.
 *   VOB/B § 13 Abs. 4 Nr. 1 S. 1: 2 Jahre; BGB: part of the Bauwerk, 5 Jahre.
 * - industrial_fire_parts: feuerberührte und abgasdämmende Teile von industriellen
 *   Feuerungsanlagen. VOB/B § 13 Abs. 4 Nr. 1 S. 2: 1 Jahr; BGB: 5 Jahre.
 * - other_work: andere Werke, deren Erfolg in der Herstellung, Wartung oder Veränderung einer
 *   Sache besteht (repairs or maintenance not essential to the building's substance).
 *   VOB/B § 13 Abs. 4 Nr. 1 S. 1: 2 Jahre; BGB § 634a Abs. 1 Nr. 1: 2 Jahre.
 * - custom: a period agreed in the contract (the months are entered).
 * Whether work counts as "Bauwerk" is a legal question of the single case (BGH: of essential
 * importance for construction, existence or use of the building); the default is editable.
 */
const WARRANTY_RULES = {
  building: {
    months: { vob: 48, bgb: 60 },
    source: { vob: "§ 13 Abs. 4 Nr. 1 S. 1 VOB/B", bgb: "§ 634a Abs. 1 Nr. 2 BGB" }
  },
  maintenance_parts: {
    months: { vob: 24, bgb: 60 },
    source: { vob: "§ 13 Abs. 4 Nr. 2 VOB/B", bgb: "§ 634a Abs. 1 Nr. 2 BGB" }
  },
  fire_parts: {
    months: { vob: 24, bgb: 60 },
    source: { vob: "§ 13 Abs. 4 Nr. 1 S. 1 VOB/B", bgb: "§ 634a Abs. 1 Nr. 2 BGB" }
  },
  industrial_fire_parts: {
    months: { vob: 12, bgb: 60 },
    source: { vob: "§ 13 Abs. 4 Nr. 1 S. 2 VOB/B", bgb: "§ 634a Abs. 1 Nr. 2 BGB" }
  },
  other_work: {
    months: { vob: 24, bgb: 24 },
    source: { vob: "§ 13 Abs. 4 Nr. 1 S. 1 VOB/B", bgb: "§ 634a Abs. 1 Nr. 1 BGB" }
  },
  custom: null
};
const WARRANTY_CATEGORIES = Object.keys(WARRANTY_RULES);

// VOB/B § 13 Abs. 5 Nr. 1: a defect claimed in writing gets its own 2 years from receipt of the
// claim, and the repaired work 2 years from the Abnahme of the repair, never ending before the
// regular period.
const VOB_CLAIM_MONTHS = 24;
// VOB/B § 17 Abs. 8 Nr. 2: an unused Sicherheit für Mängelansprüche is returned after 2 years
// unless another time was agreed (claims not yet fulfilled may hold back a matching part).
const VOB_SECURITY_RETURN_MONTHS = 24;

// ---------------------------------------------------------------------------------------------
// Calendar days
// ---------------------------------------------------------------------------------------------

const pad2 = n => String(n).padStart(2, "0");

const toIso = (y, m, d) => `${String(y).padStart(4, "0")}-${pad2(m)}-${pad2(d)}`;

/** A real calendar day "YYYY-MM-DD" (2026-02-29 is not). */
const isIsoDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [y, m, d] = String(value).split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

const partsOf = iso => String(iso).split("-").map(Number);

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const isLeapYear = year => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const addDays = (iso, days) => {
  const [y, m, d] = partsOf(iso);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

/**
 * The same day number `months` later (or earlier), clamped to the end of a shorter month:
 * § 188 Abs. 2 and 3 BGB. 2026-01-31 + 1 → 2026-02-28, 2024-02-29 + 12 → 2025-02-28.
 */
const addMonths = (iso, months) => {
  const [y, m, d] = partsOf(iso);
  const index = y * 12 + (m - 1) + Math.trunc(Number(months) || 0);
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return toIso(year, month, Math.min(d, daysInMonth(year, month)));
};

/** Whole days from a to b (b - a). */
const daysBetween = (a, b) => {
  const [ya, ma, da] = partsOf(a);
  const [yb, mb, db] = partsOf(b);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000);
};

/** 0 = Sunday … 6 = Saturday */
const weekdayOf = iso => {
  const [y, m, d] = partsOf(iso);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

const isHoliday = (iso, holidays) => Boolean(holidays && holidays.has && holidays.has(iso));

/** Werktag in the sense of § 12 Abs. 5 VOB/B: Monday to Saturday, not a public holiday. */
const isWerktag = (iso, holidays) => weekdayOf(iso) !== 0 && !isHoliday(iso, holidays);

/** Working day for § 193 BGB: Monday to Friday, not a public holiday. */
const isWorkingDay = (iso, holidays) => {
  const weekday = weekdayOf(iso);
  return weekday !== 0 && weekday !== 6 && !isHoliday(iso, holidays);
};

/** § 193 BGB: a period ending on a Saturday, Sunday or holiday ends on the next working day. */
const shiftToWorkingDay = (iso, holidays) => {
  let day = iso;
  for (let i = 0; i < 30 && !isWorkingDay(day, holidays); i += 1) day = addDays(day, 1);
  return day;
};

/**
 * The n-th Werktag after `iso` (the day of the event itself is not counted, § 187 Abs. 1 BGB).
 * "Ablauf von 12 Werktagen nach der Mitteilung" ends with this day.
 */
const addWerktage = (iso, count, holidays) => {
  let day = iso;
  let counted = 0;
  while (counted < count) {
    day = addDays(day, 1);
    if (isWerktag(day, holidays)) counted += 1;
  }
  return day;
};

const laterOf = (a, b) => (!a ? b || "" : !b ? a : a > b ? a : b);

// ---------------------------------------------------------------------------------------------
// Abnahme
// ---------------------------------------------------------------------------------------------

const basisKey = contractBasis => (contractBasis === "vob" ? "vob" : "bgb");

/**
 * When a fiktive Abnahme takes effect, or why it does not.
 * - fictitious_completion (VOB/B § 12 Abs. 5 Nr. 1): end of the 12th Werktag after the written
 *   notice of completion (`triggerDate` = receipt of the notice), when no Abnahme was asked for.
 * - fictitious_use (VOB/B § 12 Abs. 5 Nr. 2): end of the 6th Werktag after the client started
 *   to use the work (`triggerDate`), unless agreed otherwise.
 * - fictitious_deadline (§ 640 Abs. 2 BGB): end of the deadline set after completion
 *   (`deadlineDate`, moved to the next working day by § 193 BGB) if the client did not refuse
 *   naming at least one defect. Against a consumer only when the request pointed out these
 *   consequences in Textform (§ 640 Abs. 2 S. 2 BGB): `consumerNotice`.
 * Returns { date, source } or { date: "", reason }.
 */
const deemedAcceptance = ({
  method,
  contractBasis,
  triggerDate,
  deadlineDate,
  consumerNotice,
  holidays
} = {}) => {
  if (method === "fictitious_completion" || method === "fictitious_use") {
    if (contractBasis !== "vob") return { date: "", reason: "VOB_ONLY" };
    if (!isIsoDate(triggerDate)) return { date: "", reason: "NO_TRIGGER_DATE" };
    const days = method === "fictitious_completion" ? 12 : 6;
    return {
      date: addWerktage(triggerDate, days, holidays),
      source: `§ 12 Abs. 5 Nr. ${method === "fictitious_completion" ? 1 : 2} VOB/B`
    };
  }
  if (method === "fictitious_deadline") {
    if (!isIsoDate(triggerDate)) return { date: "", reason: "NO_TRIGGER_DATE" };
    if (!isIsoDate(deadlineDate)) return { date: "", reason: "NO_DEADLINE" };
    if (deadlineDate <= triggerDate) return { date: "", reason: "DEADLINE_BEFORE_REQUEST" };
    if (contractBasis === "bgb_consumer" && !consumerNotice) {
      return { date: "", reason: "CONSUMER_NOTICE_MISSING" };
    }
    return { date: shiftToWorkingDay(deadlineDate, holidays), source: "§ 640 Abs. 2 BGB" };
  }
  return { date: "", reason: "NOT_FICTITIOUS" };
};

/** Suggested deadline for a § 640 Abs. 2 BGB request sent on `requestDate`. */
const suggestedAcceptanceDeadline = (requestDate, holidays) =>
  isIsoDate(requestDate)
    ? shiftToWorkingDay(addDays(requestDate, DEFAULT_ACCEPTANCE_DEADLINE_DAYS), holidays)
    : "";

/** Counts of an acceptance's defects by status; overdue = open past its deadline on `today`. */
const defectCounts = (defects, today = "") => {
  const counts = { total: 0, open: 0, fixed: 0, accepted: 0, overdue: 0, essential: 0 };
  for (const defect of defects || []) {
    const status = DEFECT_STATUSES.includes(defect.status) ? defect.status : "open";
    counts.total += 1;
    counts[status] += 1;
    if (defect.essential && status !== "accepted") counts.essential += 1;
    if (status === "open" && today && isIsoDate(defect.deadline) && defect.deadline < today) {
      counts.overdue += 1;
    }
  }
  return counts;
};

// ---------------------------------------------------------------------------------------------
// Gewährleistung
// ---------------------------------------------------------------------------------------------

/** Statutory months and source for a category and contract basis (null for custom). */
const statutoryPeriod = (category, contractBasis) => {
  const rule = WARRANTY_RULES[category];
  if (!rule) return null;
  const key = basisKey(contractBasis);
  return { months: rule.months[key], source: rule.source[key] };
};

const cleanMonths = value => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n > 0 && n <= MAX_WARRANTY_MONTHS ? n : 0;
};

/**
 * One warranty period: months (the entered ones win over the statutory ones), start (the
 * acceptance, or a later start entered for this part), end per § 188 BGB, end moved by § 193.
 */
const warrantyPeriod = ({ category, months, start, contractBasis, holidays } = {}) => {
  const statutory = statutoryPeriod(category, contractBasis);
  const entered = cleanMonths(months);
  const effective = entered || (statutory ? statutory.months : 0);
  const out = {
    category: WARRANTY_CATEGORIES.includes(category) ? category : "custom",
    months: effective,
    statutoryMonths: statutory ? statutory.months : null,
    source: entered && (!statutory || entered !== statutory.months) ? "" : statutory?.source || "",
    agreed: Boolean(entered && (!statutory || entered !== statutory.months)),
    start: isIsoDate(start) ? start : "",
    end: "",
    endShifted: ""
  };
  if (out.start && effective) {
    out.end = addMonths(out.start, effective);
    out.endShifted = shiftToWorkingDay(out.end, holidays);
  }
  return out;
};

/** "pending" (no acceptance yet), "active", "expiring" (inspection window), "expired". */
const periodState = (end, today, leadMonths = INSPECTION_LEAD_MONTHS) => {
  if (!isIsoDate(end)) return "pending";
  if (isIsoDate(today) && end < today) return "expired";
  if (isIsoDate(today) && addMonths(end, -leadMonths) <= today) return "expiring";
  return "active";
};

/**
 * When the claim for one defect becomes time-barred. VOB/B § 13 Abs. 5 Nr. 1: 2 years from
 * the receipt of a written claim, and 2 years from the Abnahme of the repair, each never before
 * the regular end. Under BGB the period is suspended while the parties negotiate (§ 203) or
 * restarts on an acknowledgement (§ 212): a legal question of the case, so only the regular end
 * is returned with `bgbNote`.
 */
const claimLimitationEnd = ({
  contractBasis,
  regularEnd,
  receivedDate,
  writtenNotice,
  fixAcceptedDate
} = {}) => {
  if (!isIsoDate(regularEnd)) return { end: "", source: "" };
  if (contractBasis !== "vob") return { end: regularEnd, source: "", bgbNote: true };
  let end = regularEnd;
  let source = "";
  if (writtenNotice && isIsoDate(receivedDate) && receivedDate <= regularEnd) {
    const extended = addMonths(receivedDate, VOB_CLAIM_MONTHS);
    if (extended > end) {
      end = extended;
      source = "§ 13 Abs. 5 Nr. 1 S. 2 VOB/B";
    }
  }
  if (isIsoDate(fixAcceptedDate)) {
    const restarted = addMonths(fixAcceptedDate, VOB_CLAIM_MONTHS);
    if (restarted > end) {
      end = restarted;
      source = "§ 13 Abs. 5 Nr. 1 S. 3 VOB/B";
    }
  }
  return { end, source };
};

/**
 * Everything the warranty page shows: each period with its end and state, the overall end
 * (latest period), the inspection dates (one per distinct end, INSPECTION_LEAD_MONTHS before),
 * the claims with their own limitation end, open claim count and cost.
 */
const summarizeWarranty = (
  { acceptanceDate, contractBasis, periods, claims } = {},
  { today = "", holidays = null } = {}
) => {
  const start = isIsoDate(acceptanceDate) ? acceptanceDate : "";
  const items = (periods || []).map(period => {
    const computed = warrantyPeriod({
      category: period.category,
      months: period.months,
      start: isIsoDate(period.start) ? laterOf(period.start, start) : start,
      contractBasis,
      holidays
    });
    return {
      _id: period._id,
      label: period.label || "",
      ...computed,
      state: periodState(computed.end, today)
    };
  });
  const ends = items.map(i => i.end).filter(Boolean);
  const end = ends.length ? ends.reduce(laterOf) : "";
  const distinctEnds = [...new Set(ends)].sort();
  const inspections = distinctEnds.map(e => ({
    end: e,
    date: addMonths(e, -INSPECTION_LEAD_MONTHS),
    labels: items.filter(i => i.end === e).map(i => i.label || i.category)
  }));
  const claimList = (claims || []).map(claim => {
    const period = items.find(i => String(i._id) === String(claim.period)) || null;
    const regularEnd = period ? period.end : end;
    const limitation = claimLimitationEnd({
      contractBasis,
      regularEnd,
      receivedDate: claim.receivedDate,
      writtenNotice: claim.writtenNotice,
      fixAcceptedDate: claim.status === "accepted" ? claim.fixAcceptedDate : ""
    });
    const openish = ["open", "in_progress"].includes(claim.status || "open");
    return {
      _id: claim._id,
      status: claim.status || "open",
      receivedDate: claim.receivedDate || "",
      deadline: claim.deadline || "",
      overdue: Boolean(openish && today && isIsoDate(claim.deadline) && claim.deadline < today),
      lateClaim: Boolean(
        isIsoDate(claim.receivedDate) && regularEnd && claim.receivedDate > regularEnd
      ),
      limitationEnd: limitation.end,
      limitationSource: limitation.source || "",
      cost: Math.max(0, Math.round(Number(claim.cost) || 0))
    };
  });
  return {
    start,
    end,
    state: periodState(end, today),
    periods: items,
    inspections,
    // The first period that has not ended yet (its inspection may already be due)
    nextInspection: inspections.find(i => !today || i.end >= today) || null,
    claims: claimList,
    openClaims: claimList.filter(c => ["open", "in_progress"].includes(c.status)).length,
    overdueClaims: claimList.filter(c => c.overdue).length,
    claimCost: claimList.reduce((sum, c) => sum + c.cost, 0)
  };
};

/** Default periods for a new warranty: one Bauwerk period unless other categories are named. */
const defaultPeriods = (categories, contractBasis) =>
  (categories && categories.length ? categories : ["building"])
    .filter(c => WARRANTY_CATEGORIES.includes(c) && c !== "custom")
    .map(category => ({
      category,
      label: "",
      months: statutoryPeriod(category, contractBasis).months
    }));

// ---------------------------------------------------------------------------------------------
// Bürgschaften
// ---------------------------------------------------------------------------------------------

/**
 * When a Bürgschaft is due back.
 * - agreedDate: a date written in the contract or the bond wins.
 * - warranty (Gewährleistungsbürgschaft): agreedMonths after the Abnahme; else VOB/B § 17
 *   Abs. 8 Nr. 2 (2 years after the Abnahme); under BGB at the end of the warranty.
 * - contract_performance (Vertragserfüllungsbürgschaft): with the Abnahme (VOB/B § 17 Abs. 8
 *   Nr. 1: once the work is accepted and the warranty security is provided).
 * - advance_payment (Anzahlungs-/Vorauszahlungsbürgschaft): once the advance is offset against
 *   the invoices (`conditionMetOn`, VOB/B § 16 Abs. 2 Nr. 2).
 * Returns { date, source } or { date: "", reason }.
 */
const guaranteeReturnDue = ({
  type,
  contractBasis,
  acceptanceDate,
  warrantyEnd,
  agreedMonths,
  agreedDate,
  conditionMetOn
} = {}) => {
  if (isIsoDate(agreedDate)) return { date: agreedDate, source: "agreed" };
  if (type === "warranty") {
    if (!isIsoDate(acceptanceDate)) return { date: "", reason: "NO_ACCEPTANCE" };
    const months = cleanMonths(agreedMonths);
    if (months) return { date: addMonths(acceptanceDate, months), source: "agreed" };
    if (contractBasis === "vob") {
      return {
        date: addMonths(acceptanceDate, VOB_SECURITY_RETURN_MONTHS),
        source: "§ 17 Abs. 8 Nr. 2 VOB/B"
      };
    }
    if (!isIsoDate(warrantyEnd)) return { date: "", reason: "NO_WARRANTY_END" };
    return { date: warrantyEnd, source: "warranty_end" };
  }
  if (type === "contract_performance") {
    if (!isIsoDate(acceptanceDate)) return { date: "", reason: "NO_ACCEPTANCE" };
    return {
      date: acceptanceDate,
      source: contractBasis === "vob" ? "§ 17 Abs. 8 Nr. 1 VOB/B" : "acceptance"
    };
  }
  if (type === "advance_payment") {
    if (!isIsoDate(conditionMetOn)) return { date: "", reason: "ADVANCE_NOT_OFFSET" };
    return { date: conditionMetOn, source: "§ 16 Abs. 2 Nr. 2 VOB/B" };
  }
  return { date: "", reason: "UNKNOWN_TYPE" };
};

/** a × b / c rounded half away from zero, for integers (cents × basis points). */
const mulDivRound = (a, b, c) => {
  const product = a * b;
  const sign = product < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(product) * 2 + c) / (2 * c)) || 0;
};

/**
 * Avalprovision: the bank's yearly fee in basis points of the amount (150 = 1.5 % p.a.), and
 * the fee accrued from `from` to `to` pro rata by days (act/365). Banks bill quarterly or
 * yearly in advance; this is the cost while the bond is out, for the overview.
 */
const avalFee = ({ amount, rateBp, from, to } = {}) => {
  const cents = Math.max(0, Math.min(MAX_CENTS, Math.round(Number(amount) || 0)));
  const bp = Math.max(0, Math.min(10000, Math.round(Number(rateBp) || 0)));
  const yearly = Math.round((cents * bp) / 10000);
  if (!isIsoDate(from) || !isIsoDate(to) || to <= from) return { yearly, accrued: 0, days: 0 };
  const days = daysBetween(from, to);
  return { yearly, accrued: mulDivRound(yearly, days, 365), days };
};

export {
  CONTRACT_BASES,
  ACCEPTANCE_METHODS,
  FICTITIOUS_METHODS,
  ACCEPTANCE_STATUSES,
  DEFECT_STATUSES,
  CLAIM_STATUSES,
  GUARANTEE_TYPES,
  GUARANTEE_DIRECTIONS,
  GUARANTEE_STATUSES,
  DEFAULT_ACCEPTANCE_DEADLINE_DAYS,
  INSPECTION_LEAD_MONTHS,
  MAX_WARRANTY_MONTHS,
  WARRANTY_RULES,
  WARRANTY_CATEGORIES,
  VOB_CLAIM_MONTHS,
  VOB_SECURITY_RETURN_MONTHS,
  isIsoDate,
  isLeapYear,
  daysInMonth,
  addDays,
  addMonths,
  daysBetween,
  weekdayOf,
  isWerktag,
  isWorkingDay,
  shiftToWorkingDay,
  addWerktage,
  deemedAcceptance,
  suggestedAcceptanceDeadline,
  defectCounts,
  statutoryPeriod,
  warrantyPeriod,
  periodState,
  claimLimitationEnd,
  summarizeWarranty,
  defaultPeriods,
  guaranteeReturnDue,
  avalFee
};
